import { anyLeague } from "../../../classificators/league/index.js";
import { DataSection, Out, RankingScopeContext, ResultDataNew, ScoringData } from "../../../enums/index.js";
import resultsSliceForPitchType from "../../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import * as PlateAppearanceResultClassifiers from '../../../classificators/plateAppearanceResult/index.js';
import sessionWithNewScoring from "../../../classificators/session/sessionWithNewScoring.js";
import StrikeoutsCalculator from "../StrikeoutsCalculator.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import RankingStat from "../../../entities/RankingStat.js";
import classifiesAsStrikeout from "../../../classificators/outType/classifiesAsStrikeout.js";

class NewStrikeoutsCalculator extends StrikeoutsCalculator{
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter,
        strikeoutClassifier, outTypeClassifier
    ) {
        if (typeof(outTypeClassifier) !== 'undefined' && typeof(outTypeClassifier) !== 'function') {
            throw new TypeError('outTypeClassifier must be an function or undefined');
        }

        super(
            context,
            customResultsProcessor,
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithNewScoring,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key 
                        ? pitchingScopeContext
                        : battingScopeContext
                    ,
                    resultsSliceForPitchType
                )
            ),
            numberFormatter,
            skipCache,
            contextualResultFilter,
            strikeoutClassifier || PlateAppearanceResultClassifiers.classifiesAsStrikeout
        );

        this.outTypeClassifier = outTypeClassifier || classifiesAsStrikeout;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const pitches = this.filterResults(slice.getSmallestSlice(), session, league);
        const output = {};
        let strikeouts = 0;

        for (let p = 0; p < pitches.length; p++) {
            const pitch = pitches[p];

            if (
                !pitch || !pitch.hasOwnProperty(ResultDataNew.Data.key)
                || typeof(pitch[ResultDataNew.Data.key]) !== 'object'
                || !pitch[ResultDataNew.Data.key][DataSection.Scoring.key]
                || typeof(pitch[ResultDataNew.Data.key][DataSection.Scoring.key]) !== 'object'
            ) {
                // TODO - log ?
                continue;
            }

            // check PA result
            const paResult = pitch[ResultDataNew.Data.key][DataSection.Scoring.key][ScoringData.PlateAppearanceResult.key];
            if (this.strikeoutClassifier(paResult)) {
                // TODO - check for substitution
                // MLB Official Rules 2018 version, Rule 9.15
                // (b) When a batter leaves the game with two strikes against him, and the substitute batter completes a strikeout, the official scorer shall charge the strikeout and the time at bat to the first batter. If the substitute batter completes the turn at bat in any other manner, including a base on balls, the official scorer shall score the action as having been that of the substitute batter.
                strikeouts ++;
                continue;
            }

            // check outs, in some cases (FoulBunt Out) we can have it here as well
            const outs = pitch[ResultDataNew.Data.key][DataSection.Scoring.key][ScoringData.Outs.key];
            if (!(outs instanceof Array)) {
                continue;
            }
            if (outs.some(o => typeof(o) === 'object' && this.outTypeClassifier(o[Out.Type.key]))) {
                strikeouts ++;
            }
        }

        if (strikeouts > 0) {
            const strikeoutsParam = this.supportedParams[0];
            output[strikeoutsParam.key] = RankingStat.fromParameter(
                strikeoutsParam, this.numberFormatter.format(strikeouts), strikeouts
            );
        }

        // now let's calculate percentages... different divider for batting and pitching
        const strikeoutPercentageWeight = this.getStrikeoutsPercentageWeight(pitches);
        if (strikeoutPercentageWeight > 0) {
            const strikeoutPercentageParam = this.supportedParams[1];
            output[strikeoutPercentageParam.key] = RankingStat.fromParameter(
                strikeoutPercentageParam, this.numberFormatter.format(strikeouts / strikeoutPercentageWeight), strikeoutPercentageWeight
            );
        }

        return output;
    }
}

export default NewStrikeoutsCalculator;